import { useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Icon, type IconName } from "../../components/ui/Icon";
import { Orb } from "../../components/ui/Orb";
import { NewGoalMenu } from "../../components/NewGoalMenu";
import { useNative } from "../../../shared/phone/media";
import type { Role } from "../../../shared/types";
import { ccApi, type AskAnswer, type Dates, type GoalNumbers, type Home, type HomeGoal, type HomeProduct } from "../../lib/ccApi";
import { suggestions } from "./home/AskSheet";
import { greeting, longToday, paceOf, rangeLabel, running, standing, totals, useNumbers } from "./home/numbers";
import { ProductNative } from "./home/ProductNative";
import { waitingRows } from "./home/waiting";
import { AskBox, Mark } from "./Layout";
import { fmt, goalNoun, shortGoal, soonestReview, usd } from "./text";
import { errText, useCcToast, useHome } from "./useCc";
import { datesInWords, dayMon, type PaceLine } from "./v2";
import { DatePick, Fold, MiniPace, PaceBar, Status, useDockInView } from "./V2Parts";
import { planDay } from "../goal/text";
import { useCan } from "../../context/AuthContext";
import "../../styles/wizard.css";
import "../../styles/cc.css";
import "../../styles/command-v2.css";

/**
 * The Command Center (Command Center V2, notes/COMMAND-CENTER-V2.md): every product, then one product, each
 * opening on one sentence for the dates picked, what needs the owner, then one line per goal with its pace.
 * A goal opens on its own page (GoalPage). Ask opens the box beside, which finds the goal a question is about.
 */

function StatusOf({ g, p }: { g: HomeGoal; p: PaceLine | null }) {
  if (g.state === "getting_ready" || g.state === "setting_up") return <Status kind="ready" />;
  if (g.state === "planning" || g.state === "plan_ready") return <Status kind="planning" />;
  return <Status kind={p?.kind ?? "on"} paused={g.state === "paused"} />;
}

function StandingLine({ goals, nums, extra }: { goals: HomeGoal[]; nums: Map<string, GoalNumbers>; extra?: ReactNode }) {
  const s = standing(goals, nums);
  return (
    <p className="goal-line">
      {s.run > 0 && (
        <span>
          <Icon name="target" />
          {s.run} goal{s.run === 1 ? "" : "s"} running
        </span>
      )}
      {s.on > 0 && (
        <span>
          <i className="dot" />
          {s.on} on pace
        </span>
      )}
      {s.behind > 0 && (
        <span>
          <i className="dot w" />
          {s.behind} behind
        </span>
      )}
      {s.ready > 0 && <span>{s.ready} getting ready</span>}
      {s.done > 0 && <span>{s.done} finished</span>}
      {extra}
    </p>
  );
}

/** The sentence a level opens on: what the money brought over the dates picked, and how the goals stand. */
function Lead({ who, goals, nums, dates }: { who: string | null; goals: HomeGoal[]; nums: Map<string, GoalNumbers>; dates: Dates }) {
  const words = datesInWords(dates, rangeLabel(dates));
  const { spent, results } = totals(goals, nums);
  const s = standing(goals, nums);
  const pace =
    s.run === 0 ? null : s.run === 1 ? (
      <>
        {" "}
        The running goal is <mark className={s.on ? "ok" : "w"}>{s.on ? "on pace" : "behind"}</mark>.
      </>
    ) : (
      <>
        {" "}
        <mark className={s.on === s.run ? "ok" : ""}>
          {s.on} of {s.run}
        </mark>{" "}
        running goals are on pace{s.behind ? <>, <mark className="w">{s.behind} behind</mark></> : null}.
      </>
    );
  return (
    <section className="lead" data-lit>
      <p>
        {spent > 0 ? (
          <>
            {who ? `${who} spent` : "You spent"} <mark>{usd(Math.round(spent / 100) * 100)}</mark> {words} and got <mark>{fmt(results)}</mark> result{results === 1 ? "" : "s"}
            {results > 0 ? (
              <>
                , <mark>{usd(Math.round(spent / results))}</mark> each
              </>
            ) : null}
            .{pace}
          </>
        ) : (
          <>
            No spend {words}.{pace}
          </>
        )}
      </p>
    </section>
  );
}

// --- Needs you ----------------------------------------------------------------------------------------

interface Row {
  key: string;
  icon: IconName;
  title: string;
  goal: string;
  to: string;
}

/** Everything waiting: decisions, plans to review, goals still getting ready, and new lessons. */
function needRows(home: Home, can: (min: Role) => boolean, lessons: number, only?: string): Row[] {
  const rows: Row[] = waitingRows(home, can, only).map((w) => ({ key: w.key, icon: w.icon, title: w.title, goal: shortGoal(w.goal.sentence), to: w.to }));
  if (lessons && !only && can("editor")) rows.push({ key: "lessons", icon: "spark", title: lessons > 1 ? `Accept or reject ${lessons} new lessons` : "Accept or reject a new lesson", goal: "Library", to: "/library/lessons" });
  return rows;
}

function Needs({ rows }: { rows: Row[] }) {
  const [all, setAll] = useState(false);
  if (!rows.length) return null;
  const shown = all ? rows : rows.slice(0, 3);
  return (
    <section className="nv" aria-label="Needs you">
      <div className="nv-h">
        Needs you<b>{rows.length}</b>
      </div>
      {shown.map((r) => (
        <Link className="nv-r" key={r.key} to={r.to}>
          <span className="nd" />
          <span className="nv-t">
            <b>{r.title}</b>
          </span>
          <em className="nv-w">{r.goal}</em>
          <Icon name="right" />
        </Link>
      ))}
      {rows.length > 3 && (
        <button type="button" className={`fold${all ? " open" : ""}`} aria-expanded={all} onClick={() => setAll((a) => !a)}>
          {all ? "Show fewer" : `${rows.length - 3} more`}
          <Icon name="chev" />
        </button>
      )}
    </section>
  );
}

// --- A goal, in one line ----------------------------------------------------------------------------------

function GoalLine({ g, n }: { g: HomeGoal; n: GoalNumbers | undefined }) {
  const p = paceOf(g, n);
  const steps = g.state === "getting_ready" && g.readyTotal ? { done: g.readyTotal - (g.readyLeft ?? 0), total: g.readyTotal } : undefined;
  const limit = g.money ? g.money.perResultMaxCents : null;
  const each = n && n.results > 0 ? n.spendCents / n.results : null;
  const live = g.adsStart ? planDay(g.adsStart.when) : null;
  return (
    <Link className="gline" to={`/goals/${encodeURIComponent(g.id)}`}>
      <StatusOf g={g} p={p} />
      <span className="nm">
        {shortGoal(g.sentence)}
        {g.needs > 0 && <span className="needpill">{g.needs}</span>}
      </span>
      <MiniPace p={p} steps={steps} />
      <span className="ct">
        {steps ? (
          <>
            <b>{steps.done}</b> of {steps.total} steps
          </>
        ) : p ? (
          <>
            <b>{fmt(p.got)}</b> of {fmt(p.target)}
          </>
        ) : null}
      </span>
      {each !== null ? (
        <span className={`ea${limit !== null && each > limit ? " over" : ""}`} title={limit !== null ? `Cost each; most ${usd(limit)}` : "Cost each"}>
          {usd(Math.round(each))}
        </span>
      ) : (
        <span className="ea dim">{g.state === "getting_ready" ? (live ? `Live ${dayMon(live)}` : "") : running(g) ? "none yet" : ""}</span>
      )}
      <Icon name="right" />
    </Link>
  );
}

// --- The box beside: which goal a question is about ----------------------------------------------------------

type Route = { kind: "busy"; q: string } | { kind: "ask"; q: string; answer: AskAnswer };

/** The box's answer when it is not sure which goal a question is about. */
function RouteBox({ route, goals, onAsk, onOpen }: { route: Route; goals: HomeGoal[]; onAsk: (goalId: string, q: string) => void; onOpen: (goalId: string | null) => void }) {
  if (route.kind === "busy")
    return (
      <div className="cc-rr">
        <Orb live />
        <div>
          <span className="typing">
            <i />
            <i />
            <i />
          </span>
        </div>
      </div>
    );
  const { q, answer } = route;
  const byId = new Map(goals.map((g) => [g.id, g]));
  const c = answer.candidates.filter((x) => byId.has(x.goalId));
  let body;
  if (c.length === 1) {
    const g = byId.get(c[0].goalId);
    body = (
      <>
        <p>
          Do you mean the <b>{goalNoun(g?.sentence ?? c[0].sentence)}</b> goal? {c[0].why}
        </p>
        <div className="cc-chips">
          {[c[0].goalId, ...goals.map((x) => x.id).filter((id) => id !== c[0].goalId)].map((id, i) => (
            <button type="button" key={id} className={i === 0 ? "pri" : ""} onClick={() => onAsk(id, q)}>
              {shortGoal(byId.get(id)?.sentence ?? "")}
            </button>
          ))}
        </div>
      </>
    );
  } else if (c.length > 1)
    body = (
      <>
        <p>Which goal is this about? Each goal keeps its own chat, so pick one to start with.</p>
        <div className="cc-chips">
          {c.map((x) => (
            <button type="button" key={x.goalId} title={x.why} onClick={() => onAsk(x.goalId, q)}>
              {shortGoal(byId.get(x.goalId)?.sentence ?? x.sentence)}
            </button>
          ))}
        </div>
      </>
    );
  else
    body = (
      <>
        <p>I only work on your goals: their plans, ads, emails and results. Pick a goal to ask about it, or start a new one.</p>
        <div className="cc-chips">
          {goals.map((g) => (
            <button type="button" key={g.id} onClick={() => onOpen(g.id)}>
              {shortGoal(g.sentence)}
            </button>
          ))}
          <button type="button" onClick={() => onOpen(null)}>
            New goal
          </button>
        </div>
      </>
    );
  return (
    <div className="cc-rr">
      <Orb live={false} />
      <div>{body}</div>
    </div>
  );
}

/** Ask, docked beside: the box finds the goal a question is about and asks it in that goal's chat. */
function useAsk(goals: HomeGoal[]) {
  const navigate = useNavigate();
  const { refresh } = useHome();
  const { toast, say } = useCcToast();
  const [open, setOpen] = useState(false);
  const [route, setRoute] = useState<Route | null>(null);
  const dockRef = useRef<HTMLElement>(null);
  useDockInView(dockRef, open);
  const askIn = async (goalId: string, q: string) => {
    setRoute({ kind: "busy", q });
    try {
      const { runId } = await ccApi.send(goalId, q);
      void refresh();
      navigate(`/goals/${encodeURIComponent(goalId)}`, { state: { runId } });
    } catch (e) {
      setRoute(null);
      say(errText(e), { done: false });
    }
  };
  const ask = async (q: string) => {
    setRoute({ kind: "busy", q });
    try {
      const answer = await ccApi.ask(q);
      if (answer.goalId) await askIn(answer.goalId, q);
      else setRoute({ kind: "ask", q, answer });
    } catch (e) {
      setRoute(null);
      say(errText(e), { done: false });
    }
  };
  const sugs = suggestions(goals);
  const button = (
    <button type="button" className={`btn sm ghost${open ? " on" : ""}`} aria-pressed={open} onClick={() => setOpen((o) => !o)}>
      <Icon name="spark" />
      <span className="bl">Ask</span>
    </button>
  );
  const panel = (
    <aside ref={dockRef} className="gchat on" aria-label="Ask about your goals" hidden={!open}>
      <header className="gchat-h">
        <span className="ki">
          <Icon name="spark" />
        </span>
        <div>
          <b>Ask about your goals</b>
          <span>It finds the goal and asks in that goal's chat</span>
        </div>
        <button type="button" className="fx" onClick={() => setOpen(false)} aria-label="Close">
          <Icon name="x" />
        </button>
      </header>
      <div className="gchat-b cc-feed">{route && <RouteBox route={route} goals={goals} onAsk={(id, q) => void askIn(id, q)} onOpen={(id) => navigate(id ? `/goals/${encodeURIComponent(id)}` : "/goals/new")} />}</div>
      <div className="gchat-f">
        {sugs.length > 0 && (
          <div className="cc-sugs">
            {sugs.map((s) => (
              <button type="button" key={s} onClick={() => void ask(s)}>
                {s}
              </button>
            ))}
          </div>
        )}
        <AskBox placeholder="Ask about one of your goals" busy={route?.kind === "busy"} onSend={(q) => void ask(q)} />
      </div>
    </aside>
  );
  // Why: what is asked here goes into a goal's chat, which is an Editor's.
  const may = useCan()("editor");
  return { open, button: may && button, panel: may && panel, toast };
}

// --- Level 1: every product ---------------------------------------------------------------------------------

function ProductCard({ p, nums }: { p: HomeProduct; nums: Map<string, GoalNumbers> }) {
  const spent = p.goals.reduce((t, g) => t + (nums.get(g.id)?.spendCents ?? 0), 0);
  return (
    <section className="card pg" data-lit>
      <header className="pg-h">
        <Link className="hlink" to={`/command/${encodeURIComponent(p.id)}`}>
          <Mark p={p} />
          <b>
            {p.name}
            <Icon name="right" />
          </b>
        </Link>
        <span className="pg-s">
          <b>{usd(Math.round(spent / 100) * 100)}</b> spent
        </span>
        <span className="pg-new">
          <NewGoalMenu className="btn sm ghost" label="New goal" to={`/products/${encodeURIComponent(p.id)}/goals/new`} coldEmail={p.coldEmail} />
        </span>
      </header>
      {p.goals.length ? p.goals.map((g) => <GoalLine key={g.id} g={g} n={nums.get(g.id)} />) : <p className="pg-none">No goals yet.</p>}
    </section>
  );
}

/** The Command Center (`/command`) on a computer: one sentence, what needs you, then each product and its goals, one line each. */
export function CommandCenter() {
  const { home, error } = useHome();
  const [dates, setDates] = useState<Dates>({ preset: "last_7d" });
  const nums = useNumbers(dates);
  const goals = useMemo(() => home?.products.flatMap((p) => p.goals) ?? [], [home]);
  const a = useAsk(goals);
  const can = useCan();
  const next = soonestReview(goals);
  return (
    <div className="wrap ga gw ccx cv2 cvh">
      <div className="ctx">
        <div className="crumb">
          <span className="day">{longToday()}</span>
        </div>
        <div className="acts">
          {a.button}
          <NewGoalMenu to="/goals/new" coldEmail={!!home?.products.some((p) => p.coldEmail)} />
        </div>
      </div>
      <div className="live-h">
        <h1>{greeting()}</h1>
        <DatePick dates={dates} onChange={setDates} launchedAt={null} />
      </div>
      <StandingLine goals={goals} nums={nums} extra={next ? <span className="status">Next review {next}.</span> : null} />
      <div className={`live-wrap${a.open ? " chat-on" : ""}`}>
        <div className="live-main">
          {!home ? (
            <p className="load-note">{error ?? "Loading your goals…"}</p>
          ) : (
            <>
              <Lead who={null} goals={goals} nums={nums} dates={dates} />
              <Needs rows={needRows(home, can, home.lessonsWaiting ?? 0)} />
              {home.products.map((p) => (
                <ProductCard key={p.id} p={p} nums={nums} />
              ))}
            </>
          )}
        </div>
        {a.panel}
      </div>
      {a.toast}
    </div>
  );
}

// --- Level 2: one product -------------------------------------------------------------------------------------

function GoalCard({ g, n, dates }: { g: HomeGoal; n: GoalNumbers | undefined; dates: Dates }) {
  const p = paceOf(g, n);
  const label = rangeLabel(dates);
  const limit = g.money?.perResultMaxCents ?? null;
  const each = n && n.results > 0 ? n.spendCents / n.results : null;
  const by = g.result ? new Date(`${g.result.by}T00:00:00`) : null;
  const left = by ? Math.max(0, Math.round((by.getTime() - new Date(new Date().toDateString()).getTime()) / 864e5)) : null;
  return (
    <article className="card gc2" data-lit>
      <header className="gc2-h">
        <Link className="hlink" to={`/goals/${encodeURIComponent(g.id)}`}>
          <b>
            {shortGoal(g.sentence)}
            <Icon name="right" />
          </b>
        </Link>
        {g.needs > 0 && <span className="needpill">{g.needs}</span>}
      </header>
      <div className="gc2-s">
        <StatusOf g={g} p={p} />
        {by && <span>by {dayMon(by)}{left !== null && running(g) ? ` · ${left} day${left === 1 ? "" : "s"} left` : ""}</span>}
      </div>
      {p && g.result && <PaceBar p={p} label={g.result.label} />}
      {g.state === "getting_ready" && g.readyTotal ? <MiniPace p={null} steps={{ done: g.readyTotal - (g.readyLeft ?? 0), total: g.readyTotal }} /> : null}
      {running(g) && n && (
        <p className="say">
          {n.spendCents > 0 ? (
            <>
              <mark>{usd(Math.round(n.spendCents / 100) * 100)}</mark> spent {datesInWords(dates, label)} · <mark>{fmt(n.results)}</mark> result{n.results === 1 ? "" : "s"}
              {each !== null && (
                <>
                  {" "}
                  · <mark className={limit !== null && each > limit ? "w" : ""}>{usd(Math.round(each))}</mark> each{limit !== null && <span className="lim">most {usd(limit)}</span>}
                </>
              )}
            </>
          ) : (
            <>No spend {datesInWords(dates, label)}.</>
          )}
        </p>
      )}
    </article>
  );
}

/** One product in the Command Center (`/command/:productId`); a phone and an iPad draw it as a native screen. */
export function CommandProduct() {
  return useNative() ? <ProductNative /> : <CommandProductWide />;
}

/** One product on a computer: its sentence, what needs you, and each goal with its pace. */
function CommandProductWide() {
  const { productId = "" } = useParams();
  const { home, error } = useHome();
  const [dates, setDates] = useState<Dates>({ preset: "last_7d" });
  const nums = useNumbers(dates);
  const [finished, setFinished] = useState(false);
  const p = home?.products.find((x) => x.id === productId) ?? null;
  const goals = p?.goals ?? [];
  const a = useAsk(goals);
  const can = useCan();
  const live = goals.filter((g) => g.state !== "done");
  const done = goals.filter((g) => g.state === "done");
  return (
    <div className="wrap ga gw ccx cv2 cvh">
      <div className="ctx">
        <div className="crumb">
          <Link to="/command">
            <Icon name="left" />
            Command Center
          </Link>
        </div>
        <div className="acts">
          <Link className="btn sm quiet" to={`/products/${encodeURIComponent(productId)}`}>
            <Icon name="doc" />
            <span className="bl">Product page</span>
          </Link>
          {a.button}
          {p && <NewGoalMenu to={`/products/${encodeURIComponent(p.id)}/goals/new`} coldEmail={p.coldEmail} />}
        </div>
      </div>
      <div className="live-h">
        <h1>
          {p && <Mark p={p} />}
          {p?.name ?? ""}
        </h1>
        <DatePick dates={dates} onChange={setDates} launchedAt={null} />
      </div>
      <StandingLine goals={goals} nums={nums} />
      <div className={`live-wrap${a.open ? " chat-on" : ""}`}>
        <div className="live-main">
          {!home ? (
            <p className="load-note">{error ?? "Loading the product…"}</p>
          ) : !p ? (
            <p className="load-note">This product is not in your account.</p>
          ) : (
            <>
              <Lead who={p.name} goals={goals} nums={nums} dates={dates} />
              <Needs rows={needRows(home, can, 0, p.id)} />
              {live.map((g) => (
                <GoalCard key={g.id} g={g} n={nums.get(g.id)} dates={dates} />
              ))}
              {!goals.length && <p className="pg-none">No goals yet.</p>}
              {done.length > 0 && (
                <section className="card quiet-part fold-card">
                  <Fold label={`Finished goals · ${done.length}`} open={finished} onToggle={() => setFinished((f) => !f)}>
                    {done.map((g) => (
                      <GoalLine key={g.id} g={g} n={nums.get(g.id)} />
                    ))}
                  </Fold>
                </section>
              )}
              {can("admin") && (
                <section className="card part2">
                  <Link className="hlink" to={`/products/${encodeURIComponent(p.id)}/partners`}>
                    <span className="ki">
                      <Icon name="users" />
                    </span>
                    <b>
                      Partners
                      <Icon name="right" />
                    </b>
                  </Link>
                  <p>People who refer {p.name} for a share</p>
                </section>
              )}
            </>
          )}
        </div>
        {a.panel}
      </div>
      {a.toast}
    </div>
  );
}
